import { useGetFormsOfSalaryOptions } from "../hooks/useGetFormsOfSalaryOptions.ts";
import { useGetEventTypesOptions } from "../hooks/useGetEventTypesOptions.js";
import { usePayTypeOptions } from "../hooks/useGetAbsenceMappingOptions.js";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { capitalizeFirstLetter, lowerCaseFirstLetter } from "../../../../../common/utils.jsx";
import { ICellRendererParams, ValueFormatterParams } from "ag-grid-enterprise";
import { SalaryTypeCodeRenderer } from "@hr/organizationview/integrations/modals/SettingsProcountor/SalaryTypeCodeRenderer.tsx";
import { SelectOption } from "@hr/common/types.ts";
import { AbsenceSalaryMappingOverviewDTO } from "@hr/api/model";
import { GridOptions } from "ag-grid-community/dist/types/src/entities/gridOptions";
import Chip from "../../../../../common/chip/Chip.jsx";

const Pill = styled("span")`
  width: 100%;
  left: 0;
`;

export const useGetColumnsForEventMapping = (integration: string): GridOptions<
  AbsenceSalaryMappingOverviewDTO & { paidness: "Paid" | "Unpaid" }
>["columnDefs"] => {
  const { t } = useTranslation();
  const formsOfSalaryOptions = useGetFormsOfSalaryOptions();
  const salaryTypeOptionsForSelect = usePayTypeOptions(integration, "FULL"); // fetch pay type options based on integration
  const eventTypesOptions: Array<SelectOption & { paidness: "Paid" | "Unpaid" }> = useGetEventTypesOptions();
  const mappingEditValues = salaryTypeOptionsForSelect?.map(({ value }) => value);

  const absenceTypeRenderer = (params: ICellRendererParams) => {
    const label: SelectOption | undefined = eventTypesOptions?.find(
      (event: SelectOption) => event.value.toString() === params.value.toString(),
    );

    return <span>{label ? label.label : params.value}</span>;
  };

  const formOfSalaryMap = {
    1: { text: formsOfSalaryOptions[0].label },
    2: { text: formsOfSalaryOptions[1].label },
  };

  const paidnessRenderer = (params: ICellRendererParams) => {
    const unpaidAbsenceType = params.data.absenceType.endsWith("U") ? "Unpaid" : "Other";
    const paidness = params.data.absenceType.endsWith("P") ? "Paid" : unpaidAbsenceType;
    const unpaidVariant = paidness === "Unpaid" ? "secondary--outlined" : "primary--outlined";

    return (
      <Chip variant={paidness === "Paid" ? "secondary" : unpaidVariant}>
        {capitalizeFirstLetter(t(paidness))}
      </Chip>
    );
  };

  const salaryTypeCodeRenderer = (params: ICellRendererParams) => {
    if (!params.value) {
      return "";
    }
    const label = salaryTypeOptionsForSelect?.find((option) => option.value.toString() === params.value.toString());

    return <span style={{ maxHeight: "80%" }}>{label ? label.label : params.value}</span>;
  };

  const formOfSalaryRenderer = (params: ICellRendererParams, small: boolean) => {
    const formOfSalary: 1 | 2 = params.value;
    return formOfSalary ? (
      <span style={{ maxHeight: small ? "80%" : undefined }}>{formOfSalaryMap[formOfSalary].text}</span>
    ) : (
      ""
    );
  };

  const statusRenderer = (params: ICellRendererParams, isOption = false) => (
    <Chip variant={params.value ? "success" : "default"} isOption={isOption}>
      {params.value ? t("Active") : t("Inactive")}
    </Chip>
  );

  return [
    {
      headerName: t("Event"),
      field: "absenceType",
      editable: false,
      cellRenderer: absenceTypeRenderer,
      filter: "agSetColumnFilter",
      filterParams: {
        cellRenderer: (params: ICellRendererParams) => {
          const label =
            eventTypesOptions &&
            eventTypesOptions.length &&
            eventTypesOptions?.find((event) => event.value.toString() === params.value.toString());
          return label ? `${label.label} (${lowerCaseFirstLetter(t(label.paidness))})` : params.value.toString();
        },
      },
    },
    {
      headerName: t("Paidness"),
      field: "paidness",
      editable: false,
      cellRenderer: paidnessRenderer,
      filter: "agSetColumnFilter",
      filterParams: {
        values: ["Paid", "Unpaid", "Other"],
        cellRenderer: (params: ICellRendererParams) => {
          return capitalizeFirstLetter(t(params.value));
        },
      },
    },
    {
      headerName: t("SalaryType", "Palkkalaji"),
      field: "salaryTypeCode",
      cellRenderer: SalaryTypeCodeRenderer(integration),
      cellEditor: "agRichSelectCellEditor",
      singleClickEdit: true,
      cellEditorParams: {
        values: mappingEditValues,
        cellRenderer: (params: ICellRendererParams) => salaryTypeCodeRenderer(params),
      },
      filter: "agSetColumnFilter",
      filterParams: {
        values: mappingEditValues,
        valueFormatter: (params: ValueFormatterParams) => {
          const label = salaryTypeOptionsForSelect?.find(
            (option) => option.value.toString() === params.value.toString(),
          );
          return label ? label.label : params.value;
        },
      },
    },
    {
      headerName: t("FormOfSalary"),
      field: "formOfSalary",
      cellRenderer: formOfSalaryRenderer,
      cellEditor: "agRichSelectCellEditor",
      singleClickEdit: true,
      cellEditorParams: {
        values: ["1", "2"],
        cellRenderer: (params: ICellRendererParams) => formOfSalaryRenderer(params, true),
      },
      filter: "agSetColumnFilter",
      filterParams: {
        values: ["1", "2"],
        cellRenderer: (params: ICellRendererParams) => {
          const formOfSalary: 1 | 2 = params.value;
          return formOfSalaryMap[formOfSalary]?.text ?? params.value;
        },
      },
    },
    {
      headerName: t("PayGroup"),
      field: "paymentGroup",
      cellEditor: "agTextCellEditor",
      singleClickEdit: true,
      cellEditorParams: {
        maxLength: 60,
      },
      filter: "agTextColumnFilter",
    },
    {
      headerName: t("Status"),
      field: "active",
      cellRenderer: statusRenderer,
      cellEditor: "agRichSelectCellEditor",
      singleClickEdit: true,
      cellEditorParams: {
        values: [true, false],
        cellRenderer: (params: ICellRendererParams) => statusRenderer(params, true),
      },
      filter: "agSetColumnFilter",
      filterParams: {
        values: [true, false],
        cellRenderer: (params: ICellRendererParams) => {
          if (typeof params.value === "string") return params.value;
          return params.value ? t("Active") : t("Inactive");
        },
      },
    },
  ];
};
